import { useState, useEffect, useCallback } from 'react';
import { WifiOff, RefreshCw, CheckCircle, AlertTriangle, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  useIsOnline,
  useIsSyncing,
  usePendingCount,
  useFailedCount,
  useSyncProgress,
} from '@/offline/hooks';
import { syncManager } from '@/offline/sync';

type BannerState = 'hidden' | 'offline' | 'syncing' | 'synced' | 'failed' | 'pending';

export function OfflineBanner() {
  const isOnline = useIsOnline();
  const isSyncing = useIsSyncing();
  const pendingCount = usePendingCount();
  const failedCount = useFailedCount();
  const syncProgress = useSyncProgress();

  const [showSyncedMessage, setShowSyncedMessage] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  // Determine banner state
  const getBannerState = (): BannerState => {
    if (!isOnline) return 'offline';
    if (isSyncing) return 'syncing';
    if (failedCount > 0) return 'failed';
    if (pendingCount > 0) return 'pending';
    if (showSyncedMessage) return 'synced';
    return 'hidden';
  };

  const bannerState = getBannerState();

  // Show synced message briefly after sync completes
  useEffect(() => {
    if (!isSyncing && isOnline && pendingCount === 0 && failedCount === 0) {
      setShowSyncedMessage(true);
      const timer = setTimeout(() => {
        setShowSyncedMessage(false);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [isSyncing, isOnline, pendingCount, failedCount]);

  // Reset dismissed state when going offline
  useEffect(() => {
    if (!isOnline) {
      setDismissed(false);
    }
  }, [isOnline]);

  const handleSync = useCallback(async () => {
    try {
      await syncManager.triggerSync();
    } catch (error) {
      console.error('Sync failed:', error);
    }
  }, []);

  const handleRetry = useCallback(async () => {
    try {
      await syncManager.triggerSync();
    } catch (error) {
      console.error('Retry failed:', error);
    }
  }, []);

  const handleDismiss = useCallback(() => {
    setDismissed(true);
  }, []);

  // Hide banner if dismissed or hidden state
  if (dismissed || bannerState === 'hidden') {
    return null;
  }

  // Banner styles based on state
  const bannerStyles = {
    offline: 'bg-amber-500 text-white',
    syncing: 'bg-blue-500 text-white',
    synced: 'bg-green-500 text-white',
    failed: 'bg-red-500 text-white',
    pending: 'bg-amber-500 text-white',
    hidden: '',
  };

  return (
    <div
      className={cn(
        'fixed top-0 left-0 right-0 z-50 px-4 py-2',
        'flex items-center justify-between gap-2 text-sm font-medium',
        'animate-in slide-in-from-top duration-300',
        bannerStyles[bannerState]
      )}
    >
      <div className="flex items-center gap-2">
        {bannerState === 'offline' && (
          <>
            <WifiOff className="h-4 w-4" />
            <span>لا يوجد اتصال بالإنترنت</span>
            {pendingCount > 0 && (
              <Badge variant="secondary" className="bg-white/20 text-white">
                {pendingCount} في الانتظار
              </Badge>
            )}
          </>
        )}

        {bannerState === 'syncing' && (
          <>
            <RefreshCw className="h-4 w-4 animate-spin" />
            <span>جاري المزامنة... {syncProgress}%</span>
          </>
        )}

        {bannerState === 'synced' && (
          <>
            <CheckCircle className="h-4 w-4" />
            <span>تمت المزامنة بنجاح</span>
          </>
        )}

        {bannerState === 'failed' && (
          <>
            <AlertTriangle className="h-4 w-4" />
            <span>فشل مزامنة {failedCount} عنصر</span>
          </>
        )}

        {bannerState === 'pending' && (
          <>
            <RefreshCw className="h-4 w-4" />
            <span>{pendingCount} تغييرات في انتظار المزامنة</span>
          </>
        )}
      </div>

      <div className="flex items-center gap-2">
        {bannerState === 'failed' && (
          <Button
            size="sm"
            variant="ghost"
            className="h-7 px-2 text-white hover:bg-white/20"
            onClick={handleRetry}
          >
            إعادة المحاولة
          </Button>
        )}

        {bannerState === 'pending' && isOnline && (
          <Button
            size="sm"
            variant="ghost"
            className="h-7 px-2 text-white hover:bg-white/20"
            onClick={handleSync}
          >
            مزامنة الآن
          </Button>
        )}

        {(bannerState === 'failed' || bannerState === 'pending') && (
          <Button
            size="icon"
            variant="ghost"
            className="h-7 w-7 text-white hover:bg-white/20"
            onClick={handleDismiss}
          >
            <X className="h-4 w-4" />
          </Button>
        )}
      </div>
    </div>
  );
}
